import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { PaymentService } from "../../services/PaymentService";

export type ReminderLanguage = "english" | "urdu";

const BRAND_MAIN = "#fcca4a";
const BRAND_DARK = "#2d2d2d";
const BRAND_GRAY = "#4a4a4a";
const BRAND_BLACK = "#1a1a1a";
const BRAND_LIGHT = "#f5f5f5";

type ReminderLabels = {
    noticeTitle: string;
    to: string;
    date: string;
    status: string;
    greeting: string;
    greetingSuffix: string;
    reminderBody: string;
    dateCol: string;
    typeCol: string;
    descriptionCol: string;
    debitCol: string;
    creditCol: string;
    totalOutstanding: string;
    amountCol: string;
    paymentInstruction: string;
    paymentTerms: string;
    paymentTermsBody: string;
    authorizedSignature: string;
    poweredBy: string;
    paymentType: string;
    saleType: string;
    debitType: string;
    creditType: string;
    paymentReceived: string;
    dash: string;
    whatsappShareText: string;
};

const LABELS: Record<ReminderLanguage, ReminderLabels> = {
    english: {
        noticeTitle: "PAYMENT REMINDER NOTICE",
        to: "To",
        date: "Date",
        status: "OVERDUE",
        greeting: "Dear",
        greetingSuffix: "",
        reminderBody:
            "This is a friendly reminder that you have an outstanding balance of {amount} on your account. We kindly request you to settle this amount at your earliest convenience.",
        dateCol: "DATE",
        typeCol: "TYPE",
        descriptionCol: "DESCRIPTION",
        debitCol: "DEBIT (+)",
        creditCol: "CREDIT (-)",
        totalOutstanding: "TOTAL OUTSTANDING",
        amountCol: "AMOUNT",
        paymentInstruction:
            "Please make payment to our bank account or visit our shop to clear your outstanding dues.",
        paymentTerms: "PAYMENT TERMS",
        paymentTermsBody:
            "This is a computer-generated statement. Please verify all transactions and contact us immediately if you find any discrepancies. For any queries regarding this statement, please contact us using the details provided above.",
        authorizedSignature: "Authorized Signature",
        poweredBy: "POWERED BY",
        paymentType: "Payment",
        saleType: "Credit (Sale)",
        debitType: "Debit",
        creditType: "Credit",
        paymentReceived: "Payment received",
        dash: "—",
        whatsappShareText:
            "Please find your payment reminder attached. Kindly clear the outstanding balance at your earliest convenience.",
    },
    urdu: {
        noticeTitle: "ادائیگی کی یاد دہانی",
        to: "نام",
        date: "تاریخ",
        status: "واجب الادا",
        greeting: "محترم",
        greetingSuffix: "صاحب",
        reminderBody:
            "یہ ایک مؤدبانہ یاد دہانی ہے کہ آپ کے اکاؤنٹ پر {amount} کی واجب الادا رقم باقی ہے۔ برائے مہربانی اپنی سہولت کے مطابق یہ رقم جلد از جلد ادا کر دیں۔",
        dateCol: "تاریخ",
        typeCol: "قسم",
        descriptionCol: "تفصیل",
        debitCol: "واجب (+)",
        creditCol: "ادا شدہ (-)",
        totalOutstanding: "کل باقی رقم",
        amountCol: "رقم",
        paymentInstruction:
            "برائے مہربانی ہمارے بینک اکاؤنٹ میں ادائیگی کریں یا بقایا رقم کی ادائیگی کے لیے ہماری دکان پر تشریف لائیں۔",
        paymentTerms: "ادائیگی کی شرائط",
        paymentTermsBody:
            "یہ کمپیوٹر سے تیار کردہ اسٹیٹمنٹ ہے۔ برائے مہربانی تمام لین دین کی تصدیق کریں۔ کسی قسم کی غلطی کی صورت میں فوری طور پر ہم سے رابطہ کریں۔",
        authorizedSignature: "مجاز دستخط",
        poweredBy: "تیار کردہ از",
        paymentType: "ادائیگی",
        saleType: "ادھار (فروخت)",
        debitType: "ڈیبٹ",
        creditType: "کریڈٹ",
        paymentReceived: "ادائیگی موصول ہوئی",
        dash: "—",
        whatsappShareText:
            "برائے مہربانی منسلکہ ادائیگی کی یاد دہانی دیکھیں اور بقایا رقم جلد از جلد ادا کر دیں۔",
    },
};

export const formatReminderAmount = (value: number) => {
    const amount = Math.abs(value || 0);
    return `Rs ${amount.toLocaleString("en-PK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        numberingSystem: "latn",
    } as Intl.NumberFormatOptions)}`;
};

const wrapLtr = (value: string) =>
    `<span class="num-ltr" dir="ltr">${value}</span>`;

const formatReminderDate = (
    value: string | Date,
    _language: ReminderLanguage = "english"
) => {
    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        numberingSystem: "latn",
    } as Intl.DateTimeFormatOptions).format(new Date(value));
};

const getRowAmounts = (row: any) => ({
    debit: Number(row?.debit) || 0,
    credit: Number(row?.credit) || 0,
});

const getTransactionType = (
    row: any,
    labels: ReminderLabels
): { type: string; description: string } => {
    const { debit, credit } = getRowAmounts(row);

    if (debit > 0 && credit === 0) {
        return {
            type: labels.debitType,
            description:
                row?.name || row?.description || labels.paymentReceived,
        };
    }

    if (credit > 0 && debit === 0) {
        return {
            type: labels.creditType,
            description:
                row?.name ||
                row?.description ||
                (row?.id ? `Sale INV-${row.id}` : labels.saleType),
        };
    }

    return {
        type: row?.status || labels.paymentType,
        description: row?.description || row?.name || labels.dash,
    };
};

const buildTransactionRows = (
    transactions: any[],
    labels: ReminderLabels,
    language: ReminderLanguage
) => {
    if (!transactions.length) {
        return `<div class="txn-empty">${labels.dash}</div>`;
    }

    return transactions
        .map((row) => {
            const { type, description } = getTransactionType(row, labels);
            const { debit: debitAmount, credit: creditAmount } =
                getRowAmounts(row);
            const debit =
                debitAmount > 0
                    ? wrapLtr(formatReminderAmount(debitAmount))
                    : "";
            const credit =
                creditAmount > 0
                    ? wrapLtr(formatReminderAmount(creditAmount))
                    : "";
            const amountHtml = debit
                ? `<span class="transaction-amount">${debit}</span>`
                : credit
                  ? `<span class="transaction-amount">${credit}</span>`
                  : `<span class="transaction-amount">${labels.dash}</span>`;

            return `
                <div class="transaction-item">
                    <div class="transaction-top">
                        <span class="txn-date">${wrapLtr(formatReminderDate(row.createdAt, language))}</span>
                        <span class="transaction-type">${type}</span>
                    </div>
                    <div class="transaction-bottom">
                        <span class="transaction-desc">${description}</span>
                        ${amountHtml}
                    </div>
                </div>
            `;
        })
        .join("");
};

type BuildReminderOptions = {
    clientName: string;
    outstandingBalance: number;
    transactions: any[];
    language: ReminderLanguage;
    orgName: string;
    orgContact?: string;
    orgAddress?: string;
    logoUrl?: string;
    reminderNoticeEnglish?: string;
    reminderNoticeUrdu?: string;
    reminderContactDetails?: string;
};

export const buildPaymentReminderHtml = ({
    clientName,
    outstandingBalance,
    transactions,
    language,
    orgName,
    orgContact = "",
    orgAddress = "",
    logoUrl = "",
    reminderNoticeEnglish = "",
    reminderNoticeUrdu = "",
    reminderContactDetails = "",
}: BuildReminderOptions) => {
    const labels = LABELS.english;
    const messageLabels = LABELS[language];
    const amountText = wrapLtr(formatReminderAmount(outstandingBalance));
    const today = wrapLtr(formatReminderDate(new Date(), language));
    const isUrdu = language === "urdu";
    const urduSpan = (text: string) =>
        isUrdu ? `<span class="urdu-text" dir="rtl">${text}</span>` : text;
    const sectionHeading = isUrdu
        ? `${labels.dateCol} / ${labels.amountCol}`
        : `${labels.dateCol} / ${labels.typeCol}`;
    const configuredNotice = (
        isUrdu
            ? reminderNoticeUrdu || reminderNoticeEnglish
            : reminderNoticeEnglish || reminderNoticeUrdu
    )?.trim();
    const reminderBody = (
        configuredNotice || messageLabels.reminderBody
    ).replace(/\{amount\}/g, wrapLtr(formatReminderAmount(outstandingBalance)));
    const greetingLine = messageLabels.greetingSuffix
        ? isUrdu
            ? `${messageLabels.greeting} ${wrapLtr(clientName)} ${messageLabels.greetingSuffix}،`
            : `${messageLabels.greeting} ${clientName} ${messageLabels.greetingSuffix},`
        : `${messageLabels.greeting} ${clientName},`;

    return `
        <!DOCTYPE html>
        <html lang="${isUrdu ? "ur" : "en"}">
            <head>
                <meta charset="UTF-8" />
                <title>${labels.noticeTitle}</title>
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Noto+Sans+Arabic:wght@400;600;700&display=swap" rel="stylesheet" />
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    html, body { background: ${BRAND_LIGHT}; overflow: visible; }
                    body {
                        font-family: Inter, Arial, Helvetica, sans-serif;
                        direction: ltr;
                        padding: 16px;
                        color: ${BRAND_BLACK};
                    }
                    .urdu-text {
                        text-transform: none !important;
                        letter-spacing: 0 !important;
                        direction: rtl;
                        unicode-bidi: embed;
                        font-family: 'Noto Sans Arabic', Arial, sans-serif;
                    }
                    .num-ltr {
                        direction: ltr;
                        unicode-bidi: isolate;
                        display: inline-block;
                    }
                    .receipt {
                        width: min(680px, 95vw);
                        max-width: 680px;
                        margin: 30px auto;
                        background: #ffffff;
                        overflow: hidden;
                        box-shadow: 0 14px 40px rgba(16, 24, 40, 0.12);
                        border: 1px solid #ececec;
                    }
                    .receipt-header {
                        background-color: ${BRAND_DARK};
                        padding: 28px 24px 20px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 20px;
                        color: #ffffff;
                    }
                    .logo {
                        width: 52px;
                        height: 52px;
                        border-radius: 16px;
                        object-fit: cover;
                        background: #ffffff;
                        display: block;
                        border: 2px solid ${BRAND_MAIN};
                    }
                    .header-title {
                        flex: 1;
                        min-width: 0;
                    }
                    .header-title .org-name {
                        margin: 0;
                        font-size: 19px;
                        font-weight: 900;
                        color: #ffffff;
                        letter-spacing: 0.2px;
                    }
                    .header-title .notice-title {
                        font-size: 12px;
                        color: rgba(255,255,255,0.8);
                        letter-spacing: ${isUrdu ? "0" : "1px"};
                        text-transform: ${isUrdu ? "none" : "uppercase"};
                        font-weight: 700;
                        margin-top: 5px;
                    }
                    .header-right {
                        text-align: right;
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                    }
                    .status-badge {
                        background-color: ${BRAND_MAIN};
                        color: ${BRAND_DARK};
                        font-size: 12px;
                        font-weight: 800;
                        padding: 8px 18px;
                        border-radius: 24px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        text-transform: uppercase;
                    }
                    .receipt-body {
                        padding: 24px;
                        background: #ffffff;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .receipt-body > * {
                        width: 100%;
                        max-width: 620px;
                    }
                    .hero-card {
                        background: linear-gradient(180deg, ${BRAND_MAIN} 0%, #f8de84 100%);
                        border-radius: 22px;
                        padding: 26px 24px;
                        margin-bottom: 24px;
                        position: relative;
                        overflow: hidden;
                        border: 1px solid rgba(0, 0, 0, 0.08);
                    }
                    .hero-card::after {
                        content: "";
                        position: absolute;
                        inset: 0;
                        background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.18), transparent 42%);
                        pointer-events: none;
                    }
                    .hero-title {
                        font-size: 11px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 1.2px;
                        color: ${BRAND_DARK};
                        margin-bottom: 10px;
                    }
                    .hero-amount {
                        font-size: 42px;
                        font-weight: 900;
                        color: ${BRAND_DARK};
                        margin-bottom: 8px;
                    }
                    .hero-subtitle {
                        font-size: 12px;
                        color: ${BRAND_DARK};
                        opacity: 0.9;
                        line-height: 1.6;
                    }
                    .grid-two {
                        display: grid;
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                        gap: 14px;
                        margin-bottom: 18px;
                    }
                    .data-card {
                        padding: 16px 18px;
                        border-radius: 16px;
                        background: #faf5e8;
                        border: 1px solid #f0e3c1;
                    }
                    .data-card.contact-card {
                        background: #e2e2e2;
                        border-color: #cacaca;
                        border-radius: 0;
                    }
                    .data-label {
                        display: block;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 0.6px;
                        color: ${BRAND_GRAY};
                        margin-bottom: 6px;
                    }
                    .data-value {
                        font-size: 14px;
                        color: ${BRAND_DARK};
                        font-weight: 700;
                        word-break: break-word;
                    }
                    .amount-section {
                        padding: 22px 20px;
                        border-radius: 18px;
                        background-color: ${BRAND_MAIN};
                        border: 1px solid ${BRAND_DARK};
                        margin-bottom: 22px;
                    }
                    .amount-label {
                        display: block;
                        font-size: 11px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        color: ${BRAND_DARK};
                        margin-bottom: 8px;
                        font-weight: 700;
                    }
                    .amount-value {
                        font-size: 34px;
                        color: ${BRAND_BLACK};
                        font-weight: 900;
                        letter-spacing: 0.4px;
                    }
                    .message {
                        padding: 18px 20px;
                        border-radius: 18px;
                        background: #fff9e1;
                        border: 1px solid rgba(250, 205, 85, 0.35);
                        color: ${BRAND_DARK};
                        font-size: 12px;
                        line-height: 1.75;
                        margin-bottom: 24px;
                    }
                    .status-pill {
                        background-color: ${BRAND_MAIN};
                        color: ${BRAND_DARK};
                        padding: 8px 18px;
                        border-radius: 999px;
                        font-size: 11px;
                        font-weight: 800;
                        text-transform: uppercase;
                        letter-spacing: 0.8px;
                        box-shadow: 0 8px 20px rgba(252, 202, 74, 0.15);
                    }
                    .title-group {
                        flex: 1;
                        min-width: 0;
                    }
                    .title-group .org-name {
                        margin: 0;
                        font-size: 19px;
                        font-weight: 900;
                        color: #ffffff;
                        letter-spacing: 0.2px;
                    }
                    .title-group .subtitle {
                        font-size: 12px;
                        color: rgba(255, 255, 255, 0.8);
                        letter-spacing: ${isUrdu ? "0" : "1px"};
                        text-transform: ${isUrdu ? "none" : "uppercase"};
                        font-weight: 700;
                        margin-top: 5px;
                    }
                    .transaction-list {
                        display: grid;
                        gap: 10px;
                        margin-bottom: 20px;
                    }
                    .transaction-item {
                        padding: 18px 20px;
                        border-radius: 18px;
                        border: 1px solid #ece7dc;
                        background: #ffffff;
                        box-shadow: 0 0 0 rgba(0, 0, 0, 0);
                    }
                    .transaction-top {
                        display: flex;
                        justify-content: space-between;
                        gap: 12px;
                        align-items: center;
                        font-size: 11px;
                        color: ${BRAND_GRAY};
                        margin-bottom: 10px;
                    }
                    .transaction-bottom {
                        display: flex;
                        justify-content: space-between;
                        gap: 12px;
                        align-items: center;
                        flex-wrap: wrap;
                    }
                    .transaction-desc {
                        font-size: 14px;
                        font-weight: 800;
                        color: ${BRAND_DARK};
                        flex: 1;
                    }
                    .transaction-amount,
                    .txn-value {
                        font-size: 14px;
                        font-weight: 700;
                        color: ${BRAND_DARK};
                    }
                    .transaction-type,
                    .txn-type {
                        font-size: 12px;
                        color: ${BRAND_GRAY};
                    }
                    .note-block {
                        padding: 18px 20px;
                        border-radius: 18px;
                        background: #fff8df;
                        border: 1px solid rgba(250, 205, 85, 0.35);
                        color: ${BRAND_DARK};
                        font-size: 12px;
                        line-height: 1.75;
                        margin-bottom: 20px;
                    }
                    .note-block.rtl {
                        direction: rtl;
                        text-align: right;
                        unicode-bidi: embed;
                    }
                    .contact-details-block {
                        width: 100%;
                        padding: 16px 18px;
                        border-radius: 16px;
                        background: #f7f7f7;
                        border: 1px solid #e6e6e6;
                        margin-bottom: 18px;
                    }
                    .contact-details-block.rtl {
                        direction: rtl;
                        text-align: right;
                        unicode-bidi: embed;
                    }
                    .contact-details-title {
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: 0.8px;
                        text-transform: uppercase;
                        color: ${BRAND_GRAY};
                        margin-bottom: 8px;
                    }
                    .contact-details-text {
                        font-size: 13px;
                        line-height: 1.7;
                        color: ${BRAND_DARK};
                    }
                    .contact-row {
                        display: flex;
                        gap: 12px;
                        margin-bottom: 8px;
                        font-size: 13px;
                        line-height: 1.6;
                    }
                    .contact-row.rtl {
                        flex-direction: row-reverse;
                        text-align: right;
                    }
                    .contact-label {
                        font-weight: 700;
                        color: ${BRAND_DARK};
                        min-width: max-content;
                    }
                    .contact-label::after {
                        content: ": ";
                    }
                    .contact-value {
                        color: ${BRAND_DARK};
                        word-break: break-word;
                    }
                    .receipt-footer {
                        padding: 18px 24px 26px;
                        background-color: #faf7ec;
                        text-align: center;
                        color: ${BRAND_GRAY};
                        border-top: 1px solid #f0e3c3;
                        font-size: 12px;
                    }
                    .receipt-footer strong {
                        color: ${BRAND_DARK};
                    }
                </style>
            </head>
            <body>
                <div class="receipt" id="reminder-page">
                    <div class="receipt-header" style="background-color: ${BRAND_DARK}; border-bottom: 4px solid ${BRAND_MAIN};">
                        ${
                            logoUrl
                                ? `<img src="${logoUrl}" alt="Logo" class="logo" />`
                                : `<div class="logo-placeholder" aria-hidden="true"></div>`
                        }
                        <div class="header-title">
                            <div class="org-name">${orgName}</div>
                            <div class="notice-title">${labels.noticeTitle}</div>
                        </div>
                        <div class="header-right">
                            <div class="status-row">
                                <span class="status-badge">${labels.status}</span>
                            </div>
                        </div>
                    </div>

                    <div class="receipt-body">
                        <div class="hero-card">
                            <span class="hero-title">${labels.totalOutstanding}</span>
                            <div class="hero-amount">${amountText}</div>
                            <div class="hero-subtitle">${labels.paymentInstruction}</div>
                        </div>

                        <div class="grid-two">
                            <div class="data-card">
                                <span class="data-label">${labels.to}</span>
                                <span class="data-value">${clientName}</span>
                            </div>
                            <div class="data-card">
                                <span class="data-label">Sender</span>
                                <span class="data-value">${orgName}</span>
                            </div>
                        </div>

                        <div class="grid-two">
                            <div class="data-card">
                                <span class="data-label">${labels.date}</span>
                                <span class="data-value">${today}</span>
                            </div>
                            <div class="data-card">
                                <span class="data-label">Contact</span>
                                <span class="data-value">${orgContact || "-"}</span>
                            </div>
                        </div>

                        <div class="note-block${isUrdu ? " rtl" : ""}">
                            <strong>${urduSpan(greetingLine)}</strong><br /><br />
                            ${urduSpan(reminderBody)}
                        </div>

                        ${
                            reminderContactDetails?.trim()
                                ? (() => {
                                      const rows = reminderContactDetails
                                          .split(/\n+/)
                                          .map((line) => line.trim())
                                          .filter(Boolean);
                                      const pairs = rows.map((line) => {
                                          const separatorIndex =
                                              line.indexOf(":");
                                          if (separatorIndex >= 0) {
                                              return {
                                                  label: line
                                                      .slice(0, separatorIndex)
                                                      .trim(),
                                                  value: line
                                                      .slice(separatorIndex + 1)
                                                      .trim(),
                                              };
                                          }
                                          return {
                                              label: "Detail",
                                              value: line,
                                          };
                                      });
                                      return `<div class="contact-details-block">
                                          <div class="contact-details-title">Account / Contact Details</div>
                                          <div class="contact-details-text">${pairs
                                              .map(
                                                  ({ label, value }) =>
                                                      `<div class="contact-row"><span class="contact-label">${label}</span><span class="contact-value">${value}</span></div>`
                                              )
                                              .join("")}</div>
                                      </div>`;
                                  })()
                                : ""
                        }

                    </div>

                    <div class="receipt-footer">
                        <strong>${labels.poweredBy}</strong> IRA TECHNOLOGIES<br />
                        +92 342 716 9390
                    </div>
                </div>
            </body>
        </html>
    `;
};

type ReminderDataParams = {
    item: any;
    language: ReminderLanguage;
    orgName: string;
    orgContact?: string;
    orgAddress?: string;
    logoUrl?: string;
    reminderNoticeEnglish?: string;
    reminderNoticeUrdu?: string;
    reminderContactDetails?: string;
};

const fetchReminderData = async ({
    item,
    language,
    orgName,
    orgContact,
    orgAddress,
    logoUrl,
    reminderNoticeEnglish,
    reminderNoticeUrdu,
    reminderContactDetails,
}: ReminderDataParams) => {
    const clientId = item?.client?.id;
    const clientName = item?.client?.employee?.name || "Client";
    const outstandingBalance = Math.abs(item?.balance || 0);

    let transactions: any[] = [];

    if (clientId) {
        const [data] = await PaymentService.getPayments(1, 50, {
            client: clientId,
        });
        transactions = (data?.rows || []).slice(-3);
    }

    const html = buildPaymentReminderHtml({
        clientName,
        outstandingBalance,
        transactions,
        language,
        orgName,
        orgContact,
        orgAddress,
        logoUrl,
        reminderNoticeEnglish,
        reminderNoticeUrdu,
        reminderContactDetails,
    });

    return { clientName, html };
};

const waitForImages = (root: ParentNode) => {
    const images = Array.from(root.querySelectorAll("img"));
    return Promise.all(
        images.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                })
        )
    );
};

const renderReminderForCapture = async (html: string) => {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "0";
    iframe.style.top = "0";
    iframe.style.width = "432px";
    iframe.style.height = "920px";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.zIndex = "-1";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
        document.body.removeChild(iframe);
        throw new Error("Unable to render reminder");
    }

    doc.open();
    doc.write(html);
    doc.close();

    const docFonts = (doc as Document & { fonts?: { ready?: Promise<void> } })
        .fonts;
    if (docFonts?.ready) {
        await docFonts.ready.catch(() => undefined);
    }

    await new Promise((resolve) => setTimeout(resolve, 1400));
    await waitForImages(doc);

    const element = doc.getElementById("reminder-page") || doc.body;

    const contentHeight = Math.max(
        920,
        Math.max(element.scrollHeight, element.offsetHeight) + 80
    );
    iframe.style.height = `${contentHeight}px`;

    await new Promise((resolve) => setTimeout(resolve, 200));

    return { iframe, element };
};

const captureReminderCanvas = async (params: ReminderDataParams) => {
    const { clientName, html } = await fetchReminderData(params);
    const { iframe, element } = await renderReminderForCapture(html);

    try {
        const canvas = await html2canvas(element, {
            scale: 2.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#f5f5f5",
            logging: false,
            width: 432,
            height: Math.max(920, element.scrollHeight + 80),
            windowWidth: 432,
            windowHeight: Math.max(920, element.scrollHeight + 80),
            scrollX: 0,
            scrollY: 0,
        });

        if (!canvas.width || !canvas.height) {
            throw new Error("Reminder screenshot is empty");
        }

        return { canvas, clientName };
    } finally {
        document.body.removeChild(iframe);
    }
};

const canvasToPngBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error("Failed to create reminder image"));
        }, "image/png");
    });

const canvasToPdfBlob = (canvas: HTMLCanvasElement) => {
    const imgData = canvas.toDataURL("image/png");
    const pdfWidth = 100;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, pdfHeight],
    });
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    return pdf.output("blob");
};

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

export const generateReminderImageBlob = async (
    params: ReminderDataParams
): Promise<{ blob: Blob; clientName: string; fileName: string }> => {
    const { canvas, clientName } = await captureReminderCanvas(params);
    const blob = await canvasToPngBlob(canvas);
    const safeName = clientName.replace(/[^\w\s-]/g, "").trim() || "client";
    const fileName = `payment-reminder-${safeName}.png`;

    return { blob, clientName, fileName };
};

export const downloadReminderPdf = async (params: ReminderDataParams) => {
    const { canvas, clientName } = await captureReminderCanvas(params);
    const blob = canvasToPdfBlob(canvas);
    const safeName = clientName.replace(/[^\w\s-]/g, "").trim() || "client";
    const fileName = `payment-reminder-${safeName}.pdf`;
    downloadBlob(blob, fileName);
};

const normalizeWhatsappPhone = (rawPhone: string) => {
    const digits = String(rawPhone).replace(/\D/g, "");
    if (!digits) return "";
    return digits.startsWith("0") ? `92${digits.slice(1)}` : digits;
};

export const shareReminderOnWhatsapp = async (params: ReminderDataParams) => {
    const rawPhone = params.item?.client?.employee?.phone || "";
    const phone = normalizeWhatsappPhone(rawPhone);

    if (!phone) {
        window.alert("This client does not have a valid phone number.");
        return;
    }

    const labels = LABELS[params.language];
    const { blob, fileName, clientName } =
        await generateReminderImageBlob(params);

    if (blob.size < 1000) {
        window.alert(
            "Reminder image could not be generated. Please try Download PDF instead."
        );
        return;
    }

    const shareText = labels.whatsappShareText;
    const file = new File([blob], fileName, { type: "image/png" });
    const nav = navigator as Navigator & {
        canShare?: (data: any) => boolean;
        share?: (data: any) => Promise<void>;
        clipboard?: Clipboard & {
            write?: (
                data: {
                    types: string[];
                    getType: (type: string) => Promise<Blob>;
                }[]
            ) => Promise<void>;
        };
    };
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(
        navigator.userAgent
    );

    const sharePayload = { files: [file], text: shareText };

    if (nav.share && nav.canShare?.(sharePayload)) {
        try {
            await nav.share(sharePayload);
            return;
        } catch (error: any) {
            if (error?.name === "AbortError") return;
        }
    }

    if (isMobile && nav.clipboard?.write) {
        try {
            const ClipboardItemCtor = (window as any).ClipboardItem;
            if (ClipboardItemCtor) {
                await nav.clipboard.write([
                    new ClipboardItemCtor({ "image/png": blob }),
                ]);
            } else {
                throw new Error("ClipboardItem unavailable");
            }
            window.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(
                shareText
            )}`;
            window.alert(
                `Receipt copied for ${clientName}. Open WhatsApp and paste the image in the chat.`
            );
            return;
        } catch (error) {
            // fall through to download + WhatsApp
        }
    }

    downloadBlob(blob, fileName);
    const attachNote = `${shareText}\n\nReceipt saved as ${fileName}. Please attach it from your gallery.`;

    setTimeout(() => {
        const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(
            attachNote
        )}`;
        if (isMobile) {
            window.location.href = waUrl;
        } else {
            window.open(waUrl, "_blank");
        }
    }, 400);
};

export const printPaymentReminder = async (params: ReminderDataParams) => {
    const { html } = await fetchReminderData(params);
    const reminderWindow = window.open("", "_blank", "width=980,height=900");
    if (!reminderWindow) return;

    reminderWindow.document.write(html);
    reminderWindow.document.close();
    reminderWindow.focus();

    setTimeout(() => {
        reminderWindow.print();
    }, 800);
};

export const getReminderWhatsappMessage = (
    item: any,
    language: ReminderLanguage,
    orgName: string
) => {
    const labels = LABELS[language];
    const clientName = item?.client?.employee?.name || "Client";
    const amountText = formatReminderAmount(Math.abs(item?.balance || 0));
    const today = formatReminderDate(new Date());
    const body = labels.reminderBody.replace("{amount}", amountText);
    const greetingLine = labels.greetingSuffix
        ? `${labels.greeting} ${clientName} ${labels.greetingSuffix}،`
        : `${labels.greeting} ${clientName},`;

    return `${greetingLine}\n\n${body}\n\n${labels.date}: ${today}\n${orgName}`;
};
